import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useUserList, useCreateUser, useUpdateUser, useDeleteUser, useResendInvite, type CreateUserRequest } from '../../api/users';
import { applyServerErrors } from '../../lib/formErrors';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { TablePageSkeleton } from '@/components/ui/skeleton';
import { useConfirm } from '@/components/ui/confirm-dialog';
import ErrorState from '../../components/ui/ErrorState';
import { BarChart2, ShieldCheck, UserCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDate } from '../../lib/dates';
import type { Role, UserDto } from '../../types/api';

const SELECT_CLS = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring';

export default function UsersPage() {
  const [showCreate, setShowCreate] = useState(false);
  const { data: users, isLoading, error, refetch } = useUserList();
  const createUser = useCreateUser();
  const deleteUser = useDeleteUser();

  const { register, handleSubmit, setError, reset, formState: { errors } } = useForm<CreateUserRequest>();
  const { confirm, dialog } = useConfirm();

  const onSubmit = handleSubmit((values) => {
    createUser.mutate(
      { ...values, baselinePoints: Number(values.baselinePoints), baselineCycleDays: Number(values.baselineCycleDays) },
      {
        onSuccess: () => { toast.success('User created — activation email sent.'); reset(); setShowCreate(false); },
        onError:   (e) => applyServerErrors(e, setError),
      },
    );
  });

  const handleDelete = async (user: UserDto) => {
    if (!await confirm({ title: `Deactivate ${user.name}?`, description: 'Their data will be preserved.', confirmLabel: 'Deactivate' })) return;
    deleteUser.mutate(user.id, { onSuccess: () => toast.success('User deactivated.') });
  };

  if (isLoading) return <TablePageSkeleton cols={5} />;
  if (error)     return <ErrorState onRetry={refetch} />;

  return (
    <div>
      {dialog}
      <PageHeader
        title="User management"
        actions={<Button size="sm" onClick={() => setShowCreate((v) => !v)}>New user</Button>}
      />

      {showCreate && (
        <Card className="mb-5 overflow-hidden">
          <form onSubmit={onSubmit}>
            {/* Identity */}
            <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                <UserCircle className="h-4 w-4 text-primary" />
              </div>
              <p className="text-sm font-semibold text-foreground">Identity</p>
            </div>
            <div className="space-y-4 px-5 py-5">
              <div className="space-y-1.5">
                <Label>Full name <span className="text-destructive">*</span></Label>
                <Input {...register('name', { required: 'Name is required' })} />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Email <span className="text-destructive">*</span></Label>
                <Input type="email" {...register('email', { required: 'Email is required' })} />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>
            </div>

            {/* Role & access */}
            <div className="flex items-center gap-2.5 border-y border-border px-5 py-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-50 dark:bg-violet-950/40">
                <ShieldCheck className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              </div>
              <p className="text-sm font-semibold text-foreground">Role & access</p>
            </div>
            <div className="px-5 py-5">
              <div className="space-y-1.5">
                <Label>Role <span className="text-destructive">*</span></Label>
                <select {...register('role', { required: 'Role is required' })} className={SELECT_CLS}>
                  <option value="">Select role</option>
                  <option value="engineer">Engineer</option>
                  <option value="team_lead">Team Lead</option>
                  <option value="product_manager">Product Manager</option>
                  <option value="project_manager">Project Manager</option>
                  <option value="head_of_rd">Head of R&D</option>
                </select>
                {errors.role && <p className="text-xs text-destructive">{errors.role.message}</p>}
              </div>
            </div>

            {/* Workload defaults */}
            <div className="flex items-center gap-2.5 border-y border-border px-5 py-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-50 dark:bg-amber-950/40">
                <BarChart2 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <p className="text-sm font-semibold text-foreground">Workload defaults</p>
            </div>
            <div className="space-y-4 px-5 py-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Baseline points</Label>
                  <Input type="number" min={1} defaultValue={20} {...register('baselinePoints')} />
                </div>
                <div className="space-y-1.5">
                  <Label>Baseline cycle days</Label>
                  <Input type="number" min={1} defaultValue={5} {...register('baselineCycleDays')} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Team</Label>
                <Input {...register('team')} placeholder="e.g. Frontend, Backend, QA" />
              </div>
              <p className="text-xs text-muted-foreground">
                A temporary password will be generated and an activation email sent to the user.
              </p>
            </div>

            <div className="flex gap-2 border-t border-border bg-muted/30 px-5 py-3">
              <Button size="sm" type="submit" loading={createUser.isPending}>Create</Button>
              <Button size="sm" variant="ghost" type="button" onClick={() => { setShowCreate(false); reset(); }}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Team</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Lock</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users?.map((u) => (
              <UserRow key={u.id} user={u} onDelete={() => handleDelete(u)} />
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function UserRow({ user, onDelete }: { user: UserDto; onDelete: () => void }) {
  const updateUser   = useUpdateUser(user.id);
  const resendInvite = useResendInvite();
  const isLocked     = !!user.lockedUntil && new Date(user.lockedUntil) > new Date();
  const [editingTeam, setEditingTeam] = useState(false);
  const [teamDraft,   setTeamDraft]   = useState(user.team ?? '');

  const handleUnlock = () => {
    updateUser.mutate({ unlockAccount: true }, { onSuccess: () => toast.success('Account unlocked.') });
  };

  const handleToggleActive = () => {
    if (user.isActive) {
      onDelete();
    } else {
      updateUser.mutate({ isActive: true }, { onSuccess: () => toast.success('User reactivated.') });
    }
  };

  return (
    <TableRow>
      <TableCell>
        <p className="font-medium text-foreground">{user.name}</p>
        <p className="text-xs text-muted-foreground">{user.email}</p>
      </TableCell>
      <TableCell className="capitalize text-muted-foreground">{(user.role as Role).replace(/_/g, ' ')}</TableCell>
      <TableCell>
        {editingTeam ? (
          <div className="flex items-center gap-1">
            <input
              className="w-28 rounded border border-input px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              value={teamDraft}
              onChange={(e) => setTeamDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  updateUser.mutate({ team: teamDraft || '' }, { onSuccess: () => { toast.success('Team updated.'); setEditingTeam(false); } });
                } else if (e.key === 'Escape') {
                  setTeamDraft(user.team ?? ''); setEditingTeam(false);
                }
              }}
              autoFocus
            />
            <button
              type="button" className="text-xs text-primary hover:underline"
              onClick={() => updateUser.mutate({ team: teamDraft || '' }, { onSuccess: () => { toast.success('Team updated.'); setEditingTeam(false); } })}
            >
              Save
            </button>
          </div>
        ) : (
          <button type="button" className="text-xs text-muted-foreground hover:text-foreground hover:underline" onClick={() => setEditingTeam(true)}>
            {user.team ?? '—'}
          </button>
        )}
      </TableCell>
      <TableCell>
        <Badge label={user.isActive ? 'active' : 'inactive'} variant={user.isActive ? 'green' : 'gray'} />
      </TableCell>
      <TableCell>
        {isLocked
          ? <Badge label="locked" variant="red" />
          : user.failedLoginAttempts > 0
          ? <span className="text-xs text-yellow-600">{user.failedLoginAttempts} failed</span>
          : <span className="text-xs text-muted-foreground">—</span>
        }
      </TableCell>
      <TableCell className="text-muted-foreground">{formatDate(user.createdAt)}</TableCell>
      <TableCell>
        <div className="flex justify-end gap-1">
          {isLocked && (
            <Button variant="ghost" size="sm" onClick={handleUnlock} loading={updateUser.isPending}>Unlock</Button>
          )}
          {user.isActive && (
            <Button
              variant="ghost" size="sm" loading={resendInvite.isPending}
              onClick={() => resendInvite.mutate(user.id, {
                onSuccess: () => toast.success(`Invite resent to ${user.email}.`),
                onError:   () => toast.error('Failed to resend invite.'),
              })}
            >
              Resend invite
            </Button>
          )}
          <Button variant={user.isActive ? 'ghost' : 'secondary'} size="sm" onClick={handleToggleActive}>
            {user.isActive ? 'Deactivate' : 'Reactivate'}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
